import React, { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { createKey } from 'src/utils/CaseDetail.utils';
import Carousel from 'src/components/Carousel/Carousel';
import styles from './index.module.scss';
import { RootState } from 'src/store';
import { FEEDBACK_TYPES } from 'src/pages/Cases/constants/CasesConstants';
import { formatDate } from 'src/utils/commonUtils';
import NotificationBell from 'src/assets/images/icons/NotificationBell';
import { addClickstreamEvent } from '../../../../service/clickStreamEventService';
import { CLICKSTREAM_EVENT_NAMES } from '../../../../service/clickStream.constant';

const AlertsBanner: React.FC = () => {
  const { loanId = '', customerId = '' } = useParams();
  const pageData = useSelector(
    (state: RootState) => state.caseDetail.pageData?.[createKey(loanId, customerId)]
  );
  const { data: caseDetails } = pageData?.details || {};
  const { flashFeedback, flashAlerts } = caseDetails || {};
  const now = new Date();
  const currentDate = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
  const ptpDate = new Date(flashFeedback?.params.promiseDate || '');

  const PtpBanner = useCallback(() => {
    return (
      <div className={styles.slide}>
        <NotificationBell />
        <p>
          Customer has promised to pay <strong>₹{flashFeedback?.params?.promiseAmount}</strong> on{' '}
          {formatDate(flashFeedback?.params?.promiseDate || '', { year: 'numeric' })}
        </p>
      </div>
    );
  }, [flashFeedback?.params?.promiseDate]);

  const PtpUnpaidBanner = useCallback(
    () => (
      <div className={styles.slide}>
        <NotificationBell />
        <p>
          Customer had promised to pay <strong>₹ {flashFeedback?.params?.promiseAmount}</strong> on{' '}
          {flashFeedback?.params?.promiseDate} but not yet paid
        </p>
      </div>
    ),
    [flashFeedback?.params?.promiseDate]
  );

  const RequestedCallbackBanner = useCallback(
    () => (
      <div className={styles.slide}>
        <NotificationBell />
        <p>
          Customer had requested a callback on{' '}
          {formatDate(flashFeedback?.params?.callbackDate || '', { year: 'numeric' })} at{' '}
          {flashFeedback?.params?.callbackTime}
        </p>
      </div>
    ),
    [flashFeedback?.params?.callbackDate]
  );

  const CibilNotification = (props: { data: unknown }) => {
    return (
      <div className={styles.slide}>
        <NotificationBell />
        <p>{props.data as string}</p>
      </div>
    );
  };

  if (!flashFeedback && !flashAlerts?.cibilAlerts.length) return null;

  const carouselClickHandler = () => {
    addClickstreamEvent(CLICKSTREAM_EVENT_NAMES.LH_Overview_AlertBanner);
  };

  return (
    <div className={styles.bannersWrapper}>
      <div className={styles.content}>
        <Carousel
          slidesWrapperClassName={styles.slidesWrapper}
          carouselCTACallback={carouselClickHandler}
        >
          {flashFeedback?.feedbackType === FEEDBACK_TYPES.PTP && ptpDate >= currentDate && (
            <PtpBanner />
          )}
          {flashFeedback?.feedbackType === FEEDBACK_TYPES.PTP && ptpDate < currentDate && (
            <PtpUnpaidBanner />
          )}
          {flashFeedback?.feedbackType === FEEDBACK_TYPES.REQUESTED_CALLBACK && (
            <RequestedCallbackBanner />
          )}
          {caseDetails?.flashAlerts?.cibilAlerts?.map((i: unknown, index: number) => (
            <CibilNotification data={i} key={index} />
          ))}
        </Carousel>
      </div>
    </div>
  );
};

export default AlertsBanner;
